import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CodeBracketIcon,
  KeyIcon,
  CubeTransparentIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const APIReferencePage: React.FC = () => {
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(id);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const baseUrl = 'https://api.templum.io/v1';

  const endpoints = [
    {
      category: 'Auctions',
      items: [
        {
          method: 'GET',
          path: '/auctions',
          description: 'List all auctions with optional filtering',
          params: [
            { name: 'type', type: 'string', description: 'Filter by auction type (english, dutch, vickrey, candle)' },
            { name: 'status', type: 'string', description: 'Filter by status (active, ended, upcoming)' },
            { name: 'limit', type: 'number', description: 'Number of results (default: 20, max: 100)' },
            { name: 'offset', type: 'number', description: 'Pagination offset' },
          ]
        },
        {
          method: 'GET',
          path: '/auctions/{id}',
          description: 'Get detailed information about a specific auction',
          params: [
            { name: 'id', type: 'string', required: true, description: 'Auction ID or contract address' },
          ]
        },
        {
          method: 'GET',
          path: '/auctions/{id}/bids',
          description: 'Get bid history for an auction',
          params: [
            { name: 'id', type: 'string', required: true, description: 'Auction ID' },
            { name: 'limit', type: 'number', description: 'Number of results' },
          ]
        }
      ]
    },
    {
      category: 'Users',
      items: [
        {
          method: 'GET',
          path: '/users/{address}',
          description: 'Get user profile and statistics',
          params: [
            { name: 'address', type: 'string', required: true, description: 'Ethereum address' },
          ]
        },
        {
          method: 'GET',
          path: '/users/{address}/bids',
          description: 'Get bid history for a user',
          params: [
            { name: 'address', type: 'string', required: true, description: 'User wallet address' },
          ]
        },
        {
          method: 'GET',
          path: '/users/{address}/auctions',
          description: 'Get auctions created by a user',
          params: [
            { name: 'address', type: 'string', required: true, description: 'User wallet address' },
          ]
        }
      ]
    },
    {
      category: 'Staking',
      items: [
        {
          method: 'GET',
          path: '/staking/pools',
          description: 'Get all staking pool information',
          params: []
        },
        {
          method: 'GET',
          path: '/staking/user/{address}',
          description: 'Get staking positions for a user',
          params: [
            { name: 'address', type: 'string', required: true, description: 'User wallet address' },
          ]
        },
        {
          method: 'GET',
          path: '/staking/tiers',
          description: 'Get tier requirements and benefits',
          params: []
        }
      ]
    },
    {
      category: 'Governance',
      items: [
        {
          method: 'GET',
          path: '/governance/proposals',
          description: 'List all governance proposals',
          params: [
            { name: 'status', type: 'string', description: 'Filter by status (active, passed, rejected, pending)' },
          ]
        },
        {
          method: 'GET',
          path: '/governance/proposals/{id}',
          description: 'Get detailed proposal information',
          params: [
            { name: 'id', type: 'string', required: true, description: 'Proposal ID' },
          ]
        },
        {
          method: 'GET',
          path: '/governance/voting-power/{address}',
          description: 'Get voting power for an address',
          params: [
            { name: 'address', type: 'string', required: true, description: 'User wallet address' },
          ]
        }
      ]
    },
    {
      category: 'Market Data',
      items: [
        {
          method: 'GET',
          path: '/market/stats',
          description: 'Get platform-wide market statistics',
          params: []
        },
        {
          method: 'GET',
          path: '/market/ndh-price',
          description: 'Get current NDH token price',
          params: [
            { name: 'currency', type: 'string', description: 'Price currency (usd, eur, eth)' },
          ]
        }
      ]
    }
  ];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-green-500/20 text-green-400';
      case 'POST': return 'bg-blue-500/20 text-blue-400';
      case 'PUT': return 'bg-yellow-500/20 text-yellow-400';
      case 'DELETE': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <CodeBracketIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              API Reference
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-6">
              Build on TEMPLUM with our comprehensive REST API
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                REST API
              </span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                JSON Responses
              </span>
              <span className="px-4 py-2 bg-dark-800 rounded-full text-gray-300">
                Rate Limited
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Base URL */}
      <section className="py-8 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <span className="text-gray-400 text-sm">Base URL</span>
              <p className="text-white font-mono">{baseUrl}</p>
            </div>
            <button
              onClick={() => copyToClipboard(baseUrl, 'base')}
              className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
            >
              {copiedEndpoint === 'base' ? (
                <CheckIcon className="w-5 h-5 text-green-400" />
              ) : (
                <DocumentDuplicateIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Authentication */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <KeyIcon className="w-6 h-6 text-primary-400" />
              <h2 className="text-2xl font-bold text-white">Authentication</h2>
            </div>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">
                Public endpoints (read-only) do not require authentication. For rate-limited access and advanced features, include your API key in the request headers:
              </p>
              <div className="bg-dark-900 rounded-lg p-4 font-mono text-sm">
                <span className="text-gray-500">Authorization:</span> <span className="text-primary-400">Bearer YOUR_API_KEY</span>
              </div>
              <p className="text-gray-400 text-sm mt-4">
                Request an API key by contacting api@templum.io or through the developer dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <ClockIcon className="w-6 h-6 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white">Rate Limits</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <h3 className="text-lg font-semibold text-white mb-3">Public Access</h3>
                <ul className="space-y-2 text-gray-400">
                  <li>• 100 requests per minute</li>
                  <li>• 5,000 requests per day</li>
                  <li>• No authentication required</li>
                </ul>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                <h3 className="text-lg font-semibold text-white mb-3">Authenticated Access</h3>
                <ul className="space-y-2 text-gray-400">
                  <li>• 1,000 requests per minute</li>
                  <li>• 100,000 requests per day</li>
                  <li>• Priority support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <CubeTransparentIcon className="w-6 h-6 text-blue-400" />
              <h2 className="text-2xl font-bold text-white">Endpoints</h2>
            </div>

            <div className="space-y-8">
              {endpoints.map((category, cidx) => (
                <div key={cidx}>
                  <h3 className="text-xl font-semibold text-white mb-4">{category.category}</h3>
                  <div className="space-y-4">
                    {category.items.map((endpoint, eidx) => {
                      const endpointId = `${cidx}-${eidx}`;
                      return (
                        <motion.div
                          key={eidx}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          className="bg-dark-800/50 rounded-xl border border-dark-700"
                        >
                          <div className="p-4 flex items-center justify-between border-b border-dark-700">
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${getMethodColor(endpoint.method)}`}>
                                {endpoint.method}
                              </span>
                              <code className="text-white font-mono">{endpoint.path}</code>
                            </div>
                            <button
                              onClick={() => copyToClipboard(`${baseUrl}${endpoint.path}`, endpointId)}
                              className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                            >
                              {copiedEndpoint === endpointId ? (
                                <CheckIcon className="w-4 h-4 text-green-400" />
                              ) : (
                                <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
                              )}
                            </button>
                          </div>
                          <div className="p-4">
                            <p className="text-gray-400 mb-4">{endpoint.description}</p>
                            {endpoint.params.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-300 mb-2">Parameters</h4>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-gray-500">
                                      <th className="pb-2">Name</th>
                                      <th className="pb-2">Type</th>
                                      <th className="pb-2">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-gray-400">
                                    {endpoint.params.map((param, pidx) => (
                                      <tr key={pidx} className="border-t border-dark-700">
                                        <td className="py-2">
                                          <code className="text-primary-400">{param.name}</code>
                                          {(param as { required?: boolean }).required && (
                                            <span className="text-red-400 ml-1">*</span>
                                          )}
                                        </td>
                                        <td className="py-2 text-gray-500">{param.type}</td>
                                        <td className="py-2">{param.description}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Response Format */}
      <section className="py-12 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Response Format</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">All API responses follow this format:</p>
              <div className="bg-dark-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <pre className="text-gray-300">{`{
  "success": true,
  "data": {
    // Response data
  },
  "meta": {
    "timestamp": "2026-01-21T12:00:00Z",
    "requestId": "abc123"
  }
}`}</pre>
              </div>
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-white mb-3">Error Response</h3>
                <div className="bg-dark-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <pre className="text-gray-300">{`{
  "success": false,
  "error": {
    "code": "INVALID_PARAMETER",
    "message": "The 'limit' parameter must be a positive integer"
  }
}`}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WebSocket */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">WebSocket API</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">
                For real-time updates on auctions and bids, connect to our WebSocket endpoint:
              </p>
              <div className="bg-dark-900 rounded-lg p-4 font-mono text-sm mb-4">
                <span className="text-gray-500">wss://</span><span className="text-primary-400">ws.templum.io/v1</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Available Channels</h3>
              <ul className="space-y-2 text-gray-400">
                <li>• <code className="text-primary-400">auction:{'{id}'}</code> - Subscribe to auction updates</li>
                <li>• <code className="text-primary-400">bids:{'{auction_id}'}</code> - Subscribe to new bids</li>
                <li>• <code className="text-primary-400">market</code> - Subscribe to market statistics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SDKs */}
      <section className="py-12 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <ShieldCheckIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              SDKs & Libraries
            </h2>
            <p className="text-gray-300 mb-6">
              Official SDKs are coming soon for JavaScript, Python, and Go.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-dark-800 rounded-lg text-gray-400">JavaScript (Coming Soon)</span>
              <span className="px-4 py-2 bg-dark-800 rounded-lg text-gray-400">Python (Coming Soon)</span>
              <span className="px-4 py-2 bg-dark-800 rounded-lg text-gray-400">Go (Coming Soon)</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default APIReferencePage;
